import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GearSix, PlusCircle, Trash, PencilSimple, Target, Warning } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const ROLES_LABEL = {
  operativo: "Operativo", mantenimiento: "Mantenimiento", almacen: "Almacén",
  campamento: "Campamento", residente_mina: "Residente de mina",
  gerente_proyecto: "Gerente de proyecto", administrativo: "Administrativo", jefatura: "Jefatura",
};

const KpiCard = ({ label, value, hint, testid, ok = null }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
    <p className={`font-heading font-black text-3xl tracking-tight mt-1 ${ok === false ? "text-red-600" : ok === true ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
    {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

const emptyKpi = () => ({ name: "", value: "", target: "", unit: "", direction: "lower" });

export default function KpisTab({ canManage }) {
  const [k, setK] = useState(null);
  const [targets, setTargets] = useState(null);
  const [custom, setCustom] = useState([]);
  const [absentWeek, setAbsentWeek] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [kpiOpen, setKpiOpen] = useState(false);
  const [kpiForm, setKpiForm] = useState(emptyKpi());
  const [editingKpi, setEditingKpi] = useState(null);
  const [tForm, setTForm] = useState(null);

  const load = useCallback(() => {
    api.get("/hr/kpis").then((r) => setK(r.data)).catch(() => {});
    api.get("/hr/kpi-settings").then((r) => setTargets(r.data)).catch(() => {});
    api.get("/hr/custom-kpis").then((r) => setCustom(r.data)).catch(() => {});
    api.get("/hr/kpis/absent-week").then((r) => setAbsentWeek(r.data)).catch(() => setAbsentWeek({ items: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveTargets = async () => {
    try {
      const { data } = await api.put("/hr/kpi-settings", {
        absenteeism_pct: Number(tForm.absenteeism_pct), turnover_pct: Number(tForm.turnover_pct),
        certs_expired_max: Number(tForm.certs_expired_max), certs_expiring_max: Number(tForm.certs_expiring_max),
      });
      setTargets(data); setTargetsOpen(false);
      toast.success("Metas actualizadas");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveKpi = async () => {
    if (!kpiForm.name) { toast.error("Nombre requerido"); return; }
    try {
      const payload = { ...kpiForm, value: Number(kpiForm.value || 0), target: Number(kpiForm.target || 0) };
      if (editingKpi) await api.put(`/hr/custom-kpis/${editingKpi}`, payload);
      else await api.post("/hr/custom-kpis", payload);
      toast.success("KPI guardado");
      setKpiOpen(false); setEditingKpi(null); setKpiForm(emptyKpi()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeKpi = async (id) => {
    if (!window.confirm("¿Eliminar este KPI personalizado?")) return;
    try { await api.delete(`/hr/custom-kpis/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!k || !targets) return <p className="text-sm text-slate-400 text-center py-16">Cargando…</p>;

  const crewData = Object.entries(k.by_crew).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  const roleData = Object.entries(k.by_role).filter(([, v]) => v > 0).map(([name, value]) => ({ name: ROLES_LABEL[name] || name, value }));
  const CREW_PALETTE = ["#1E3A8A", "#059669", "#F59E0B", "#7C3AED", "#DC2626", "#0EA5E9", "#94A3B8"];

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      {canManage && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="kpi-edit-targets-btn"
                  onClick={() => { setTForm({ ...targets }); setTargetsOpen(true); }}>
            <GearSix size={14} weight="bold" /> Editar metas
          </Button>
          <Button size="sm" className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="kpi-add-custom-btn"
                  onClick={() => { setEditingKpi(null); setKpiForm(emptyKpi()); setKpiOpen(true); }}>
            <PlusCircle size={14} weight="bold" /> KPI personalizado
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total planilla" value={k.total} testid="hr-kpi-total" />
        <KpiCard label="Activos" value={k.active} testid="hr-kpi-active" ok={true} />
        <KpiCard label="En mina hoy" value={k.on_site_today} hint="Cobertura de turno" testid="hr-kpi-on-site" />
        <KpiCard label="HH plan mensual" value={k.planned_manhours_month.toLocaleString()} testid="hr-kpi-hh" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Ausentismo 30d" value={`${k.absenteeism_pct_30d}%`} hint={`Meta ≤ ${targets.absenteeism_pct}%`}
                 testid="hr-kpi-absent" ok={k.absenteeism_pct_30d <= targets.absenteeism_pct} />
        <KpiCard label="Rotación 90d" value={`${k.turnover_pct_90d}%`} hint={`Meta ≤ ${targets.turnover_pct}%`}
                 testid="hr-kpi-turnover" ok={k.turnover_pct_90d <= targets.turnover_pct} />
        <KpiCard label="Certificados vencidos" value={k.certs.expired} hint={`Meta ≤ ${targets.certs_expired_max}`}
                 testid="hr-kpi-cert-expired" ok={k.certs.expired <= targets.certs_expired_max} />
        <KpiCard label="Cert. por vencer 30d" value={k.certs.in_30d} hint={`Meta ≤ ${targets.certs_expiring_max}`}
                 testid="hr-kpi-cert-30" ok={k.certs.in_30d <= targets.certs_expiring_max} />
      </div>

      {custom.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1.5">
            <Target size={13} weight="bold" /> KPIs personalizados
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hr-custom-kpis">
            {custom.map((c) => {
              const ok = c.direction === "lower" ? c.value <= c.target : c.value >= c.target;
              return (
                <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4 relative group" data-testid={`custom-kpi-${c.id}`}>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 pr-10">{c.name}</p>
                  <p className={`font-heading font-black text-3xl tracking-tight mt-1 ${ok ? "text-emerald-700" : "text-red-600"}`}>
                    {c.value}{c.unit && <span className="text-base font-bold text-slate-400 ml-1">{c.unit}</span>}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Meta {c.direction === "lower" ? "≤" : "≥"} {c.target}{c.unit}</p>
                  {canManage && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => { setEditingKpi(c.id); setKpiForm({ ...c, value: String(c.value), target: String(c.target) }); setKpiOpen(true); }}
                              className="text-slate-400 hover:text-blue-800" data-testid={`custom-kpi-edit-${c.id}`}><PencilSimple size={13} /></button>
                      <button onClick={() => removeKpi(c.id)} className="text-slate-400 hover:text-red-500" data-testid={`custom-kpi-delete-${c.id}`}><Trash size={13} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {absentWeek && absentWeek.items && absentWeek.items.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4" data-testid="hr-absent-week-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-600" />
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-red-700">
                  Ausentismo semanal · ≥ {absentWeek.threshold} faltas
                </p>
                <p className="text-[10px] text-red-500">
                  Semana ISO {absentWeek.week_iso} · {absentWeek.start} → {absentWeek.end}
                </p>
              </div>
            </div>
            <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-600 text-white"
                  data-testid="hr-absent-week-count">
              {absentWeek.items.length} colaborador{absentWeek.items.length === 1 ? "" : "es"}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hr-absent-week-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-red-800 border-b border-red-200">
                  <th className="text-left py-1.5">Colaborador</th>
                  <th className="text-left py-1.5">Puesto</th>
                  <th className="text-left py-1.5">Cuadrilla</th>
                  <th className="text-center py-1.5">Faltas</th>
                  <th className="text-left py-1.5 hidden md:table-cell">Días</th>
                </tr>
              </thead>
              <tbody>
                {absentWeek.items.map((r) => (
                  <tr key={r.employee_id} className="border-b border-red-100 last:border-0"
                      data-testid={`hr-absent-row-${r.employee_id}`}>
                    <td className="py-1.5 font-semibold text-slate-900">{r.name}</td>
                    <td className="py-1.5 text-slate-600 text-xs">{r.position || "-"}</td>
                    <td className="py-1.5 text-slate-600 text-xs">{r.crew || "-"}</td>
                    <td className="py-1.5 text-center">
                      <span className="inline-block px-2 py-0.5 rounded font-mono font-bold bg-red-600 text-white text-xs">
                        {r.count}
                      </span>
                    </td>
                    <td className="py-1.5 text-[10px] font-mono text-red-700 hidden md:table-cell">
                      {r.days.join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-red-600 mt-2 italic">
            Sugerencia: revisa cada caso con la jefatura directa antes del cierre semanal para prevenir rotación.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hr-chart-crews">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Headcount por cuadrilla</p>
          {crewData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={crewData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label>
                  {crewData.map((e, i) => <Cell key={i} fill={CREW_PALETTE[i % CREW_PALETTE.length]} />)}
                </Pie>
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 py-8 text-center">Sin datos</p>}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="hr-chart-roles">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Por rol</p>
          {roleData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={roleData} layout="vertical" margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={110} />
                <ChartTooltip />
                <Bar dataKey="value" fill="#1E3A8A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 py-8 text-center">Sin datos</p>}
        </div>
      </div>

      {/* Targets dialog */}
      <Dialog open={targetsOpen} onOpenChange={setTargetsOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Metas de KPIs</DialogTitle></DialogHeader>
          {tForm && (
            <div className="space-y-3">
              {[
                { key: "absenteeism_pct", label: "Ausentismo máx. (%)" },
                { key: "turnover_pct", label: "Rotación máx. (%)" },
                { key: "certs_expired_max", label: "Certificados vencidos máx." },
                { key: "certs_expiring_max", label: "Cert. por vencer 30d máx." },
              ].map((f) => (
                <div key={f.key}>
                  <Label className="overline-label">{f.label}</Label>
                  <Input type="number" min="0" step="0.1" value={tForm[f.key]}
                         onChange={(e) => setTForm({ ...tForm, [f.key]: e.target.value })}
                         data-testid={`kpi-target-${f.key}`} />
                </div>
              ))}
              <Button onClick={saveTargets} className="bg-blue-900 hover:bg-blue-800 w-full" data-testid="kpi-targets-save">Guardar metas</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom KPI dialog */}
      <Dialog open={kpiOpen} onOpenChange={(o) => { setKpiOpen(o); if (!o) { setEditingKpi(null); setKpiForm(emptyKpi()); } }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editingKpi ? "Editar" : "Nuevo"} KPI personalizado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="overline-label">Nombre</Label>
              <Input value={kpiForm.name} onChange={(e) => setKpiForm({ ...kpiForm, name: e.target.value })}
                     placeholder="Ej. Horas de capacitación" data-testid="custom-kpi-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline-label">Valor actual</Label>
                <Input type="number" step="0.01" value={kpiForm.value} onChange={(e) => setKpiForm({ ...kpiForm, value: e.target.value })} data-testid="custom-kpi-value" /></div>
              <div><Label className="overline-label">Meta</Label>
                <Input type="number" step="0.01" value={kpiForm.target} onChange={(e) => setKpiForm({ ...kpiForm, target: e.target.value })} data-testid="custom-kpi-target" /></div>
              <div><Label className="overline-label">Unidad</Label>
                <Input value={kpiForm.unit} onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} placeholder="%, hrs, u" data-testid="custom-kpi-unit" /></div>
              <div><Label className="overline-label">Dirección</Label>
                <Select value={kpiForm.direction} onValueChange={(v) => setKpiForm({ ...kpiForm, direction: v })}>
                  <SelectTrigger data-testid="custom-kpi-direction"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="lower">Menor es mejor</SelectItem>
                    <SelectItem value="higher">Mayor es mejor</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <Button onClick={saveKpi} className="bg-blue-900 hover:bg-blue-800 w-full" data-testid="custom-kpi-save">
              {editingKpi ? "Actualizar" : "Crear KPI"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
